import { Component, type ReactNode } from "react";
import { ErrorState } from "@note-app/ui";

export interface RouteErrorBoundaryProps {
  readonly resetKey: string;
  readonly title: string;
  readonly children: ReactNode;
}

interface RouteErrorBoundaryState {
  readonly error?: unknown;
}

/** Keeps a failed lazy feature from blanking the whole local-first shell. */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  public state: RouteErrorBoundaryState = {};

  public static getDerivedStateFromError(
    error: unknown,
  ): RouteErrorBoundaryState {
    return { error };
  }

  public componentDidUpdate(previous: RouteErrorBoundaryProps): void {
    if (
      previous.resetKey !== this.props.resetKey &&
      this.state.error !== undefined
    ) {
      this.setState({ error: undefined });
    }
  }

  public render(): ReactNode {
    if (this.state.error === undefined) return this.props.children;
    const reloadRequired = isModuleLoadError(this.state.error);
    return (
      <ErrorState
        title={this.props.title}
        description={reloadRequired
          ? "编辑器文件未能加载。请确认网络或本地服务已恢复，再重新加载页面；已保存的笔记会保留。"
          : "可以尝试重新加载编辑器，已保存的本地内容不会被清除。"}
        details={errorMessage(this.state.error)}
        detailsLabel="错误详情"
        retryLabel={reloadRequired ? "重新加载页面" : "重新加载编辑器"}
        onRetry={() => {
          if (reloadRequired) {
            // React.lazy caches rejected imports. Remounting the same feature
            // cannot retry them; reload only after the user requests recovery.
            window.location.reload();
          } else {
            this.setState({ error: undefined });
          }
        }}
      />
    );
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isModuleLoadError(error: unknown): boolean {
  return (error instanceof Error && error.name === "ChunkLoadError") ||
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading (?:CSS )?chunk .+ failed|Unable to preload CSS/iu.test(errorMessage(error));
}
